/**
 * @fileoverview Step 1 - Basic user information
 * @description Collects email, password, first name and last name
 */

import { Button, Divider, PasswordInput, Space, Text, TextInput } from '@mantine/core'
import { useForm } from '@mantine/form'
import { useDisclosure } from '@mantine/hooks'
import { showNotification } from '@mantine/notifications'
import { IconX } from '@tabler/icons-react'
import { Heading } from 'components'
import { EmailAlreadyRegisteredError } from 'helpers'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useSignupContext } from '../context/SignupContext'
import { submitSignup } from '../service'
import { Form, NameFieldsRow } from '../styles'
import { GoogleSignupButton } from './GoogleSignupButton'

/**
 * Step 1 component - Basic user information
 */
export const SignupStep1 = () => {
  const { t } = useTranslation()
  const { data, setData, setStep, setUser } = useSignupContext()
  const [visible, { toggle }] = useDisclosure(false)
  const [loading, setLoading] = useState(false)

  const form = useForm({
    initialValues: {
      email: data.email,
      password: data.password,
      firstName: data.firstName,
      lastName: data.lastName,
    },
    validateInputOnBlur: true,
    validate: {
      email: value => {
        if (!value) return t('teacher_signup.validation_email_required')
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return t('teacher_signup.validation_email_invalid')
        return null
      },
      password: value => {
        if (!value) return t('teacher_signup.validation_password_required')
        // Backend (Devise) requires: minimum 6 characters, maximum 128 characters
        if (value.length < 6) return t('teacher_signup.validation_password_min')
        if (value.length > 128) return t('teacher_signup.validation_password_max')
        return null
      },
      firstName: value => (!value ? t('teacher_signup.validation_first_name_required') : null),
      lastName: value => (!value ? t('teacher_signup.validation_last_name_required') : null),
    },
  })

  /**
   * Handle form submission - calls signup API and stores user in context
   * @param values - Form values
   */
  const handleSubmit = async (values: typeof form.values) => {
    setLoading(true)
    setData(values)

    try {
      const response = await submitSignup({ ...data, ...values })
      setUser(response.user)
      setStep(2)
    } catch (error) {
      if (error instanceof EmailAlreadyRegisteredError) {
        showNotification({
          title: 'Email Already Registered',
          message: 'The email address you entered is already associated with an existing account. Please log in or use a different email to sign up.',
          color: 'red',
          icon: <IconX size={18} />,
          autoClose: 8000,
        })
        return
      }
      const errorMessage = error instanceof Error ? error.message : t('teacher_signup.signup_error')
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <div style={{ textAlign: 'center' }}>
        <Heading size='xl' text={t('teacher_signup.page_title')} />
      </div>
      <Text color='dimmed' align='center' mb='lg'>
        {t('teacher_signup.page_subtitle')}
      </Text>

      <GoogleSignupButton />

      <Divider my='lg' label={t('teacher_signup.or_divider')} labelPosition='center' />

      <Form onSubmit={form.onSubmit(handleSubmit)}>
        <TextInput
          size='md'
          label={t('teacher_signup.email_label')}
          placeholder={t('teacher_signup.email_placeholder')}
          {...form.getInputProps('email')}
        />

        <PasswordInput
          mt='md'
          size='md'
          label={t('teacher_signup.password_label')}
          placeholder={t('teacher_signup.password_placeholder')}
          visible={visible}
          onVisibilityChange={toggle}
          {...form.getInputProps('password')}
        />

        <Space h='md' />
        <NameFieldsRow>
          <TextInput
            size='md'
            style={{ flex: 1 }}
            label={t('teacher_signup.first_name_label')}
            placeholder={t('teacher_signup.first_name_placeholder')}
            {...form.getInputProps('firstName')}
          />
          <TextInput
            size='md'
            style={{ flex: 1 }}
            label={t('teacher_signup.last_name_label')}
            placeholder={t('teacher_signup.last_name_placeholder')}
            {...form.getInputProps('lastName')}
          />
        </NameFieldsRow>

        <Divider my='lg' />

        <Button
          fullWidth
          size='lg'
          type='submit'
          loading={loading}
          radius='xl'
        >
          {t('teacher_signup.sign_up_button')}
        </Button>
      </Form>
    </>
  )
}
